import { useEffect, useRef, useState } from 'react'
import { Stage, Layer, Rect, Image } from 'react-konva'
import Konva from 'konva'
import { Box, Typography } from '@mui/material'
import { GameState, TileData, MergedTileGroup, Position } from '../types'
import Tile from './Tile'
import MergedTile from './MergedTile'
import { generateTiles, areAdjacent, areInCorrectRelativePosition, checkWinCondition } from '../utils/tileGenerator'

interface GameBoardProps {
  gameState: GameState
  setGameState: React.Dispatch<React.SetStateAction<GameState>>
  uploadedImage: HTMLImageElement | null
  restartKey: number
}

// Calculate responsive tile dimensions based on viewport and aspect ratio
const calculateTileDimensions = (gridSize: number, aspectRatio: number): { width: number; height: number } => {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  // Reserve space for padding and UI elements
  const maxWidth = Math.min(viewportWidth - 32, 800) // 16px padding on each side, max 800px
  const maxHeight = viewportHeight - 200 // Reserve ~200px for header and other UI

  // Calculate tile size based on aspect ratio
  // For portrait (aspectRatio < 1): height is larger, width = height * aspectRatio
  // For landscape (aspectRatio > 1): width is larger, height = width / aspectRatio
  // For square (aspectRatio = 1): width = height

  let tileWidth: number
  let tileHeight: number

  if (aspectRatio >= 1) {
    // Landscape or square - width is limiting
    const tileSizeByWidth = Math.floor(maxWidth / gridSize)
    const tileSizeByHeight = Math.floor((maxHeight * aspectRatio) / gridSize)
    const baseSize = Math.min(tileSizeByWidth, tileSizeByHeight)
    const clampedSize = Math.max(80, Math.min(150, baseSize))

    tileWidth = clampedSize
    tileHeight = clampedSize / aspectRatio
  } else {
    // Portrait - height is limiting
    const tileSizeByWidth = Math.floor((maxWidth / aspectRatio) / gridSize)
    const tileSizeByHeight = Math.floor(maxHeight / gridSize)
    const baseSize = Math.min(tileSizeByWidth, tileSizeByHeight)
    const clampedSize = Math.max(80, Math.min(150, baseSize))

    tileHeight = clampedSize
    tileWidth = clampedSize * aspectRatio
  }

  return { width: tileWidth, height: tileHeight }
}

function GameBoard({ gameState, setGameState, uploadedImage, restartKey }: GameBoardProps) {
  const stageRef = useRef<Konva.Stage>(null)
  const dragStartPos = useRef<Position | null>(null)
  const groupDragStartPos = useRef<Position | null>(null)
  const [tileDimensions, setTileDimensions] = useState(() =>
    calculateTileDimensions(gameState.gridSize, gameState.imageAspectRatio)
  )

  // Update tile dimensions when grid size or aspect ratio changes
  useEffect(() => {
    setTileDimensions(calculateTileDimensions(gameState.gridSize, gameState.imageAspectRatio))
  }, [gameState.gridSize, gameState.imageAspectRatio])

  // Handle window resize for responsive tile sizing
  useEffect(() => {
    const handleResize = () => {
      setTileDimensions(calculateTileDimensions(gameState.gridSize, gameState.imageAspectRatio))
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [gameState.gridSize, gameState.imageAspectRatio])

  // Initialize tiles when image is uploaded, grid size changes, tile dimensions change, or restart is triggered
  useEffect(() => {
    if (uploadedImage && gameState.imageLoaded) {
      const tiles = generateTiles(uploadedImage, gameState.gridSize, tileDimensions.width, tileDimensions.height)
      setGameState(prev => ({
        ...prev,
        tiles,
        mergedGroups: [],
        isComplete: false,
      }))
    }
  }, [uploadedImage, gameState.gridSize, gameState.imageLoaded, restartKey, tileDimensions.width, tileDimensions.height, setGameState])

  const handleDragStart = (tileId: string) => {
    // Store the original position before drag
    const tile = gameState.tiles.find(t => t.id === tileId)
    if (tile) {
      dragStartPos.current = { ...tile.currentPosition }
    }
  }

  const handleDragMove = (tileId: string, x: number, y: number) => {
    // Update tile position during drag
    setGameState(prev => ({
      ...prev,
      tiles: prev.tiles.map(tile =>
        tile.id === tileId
          ? { ...tile, currentPosition: { x, y } }
          : tile
      ),
    }))
  }

  const handleDragEnd = (tileId: string, x: number, y: number) => {
    if (!dragStartPos.current) return

    const draggedOriginalPos = { ...dragStartPos.current }

    // Find if dropped on another individual tile
    const targetTile = gameState.tiles.find(tile => {
      if (tile.id === tileId) return false
      // Check if this tile is part of a merged group - if so, skip it (handle separately)
      const isInMergedGroup = gameState.mergedGroups.some(g => g.tileIds.includes(tile.id))
      if (isInMergedGroup) return false

      const dx = Math.abs(tile.currentPosition.x - x)
      const dy = Math.abs(tile.currentPosition.y - y)
      // Use stricter threshold - tiles must overlap significantly to swap
      return dx < tileDimensions.width / 3 && dy < tileDimensions.height / 3
    })

    // Find if dropped on a merged group
    const targetGroup = gameState.mergedGroups.find(group => {
      const groupTiles = gameState.tiles.filter(t => group.tileIds.includes(t.id))
      return groupTiles.some(tile => {
        const dx = Math.abs(tile.currentPosition.x - x)
        const dy = Math.abs(tile.currentPosition.y - y)
        // Use stricter threshold - tiles must overlap significantly to swap
        return dx < tileDimensions.width / 3 && dy < tileDimensions.height / 3
      })
    })

    if (targetTile) {
      // Swap with individual tile
      const targetPos = { ...targetTile.currentPosition }

      setGameState(prev => {
        const updatedTiles = prev.tiles.map(tile => {
          if (tile.id === tileId) {
            return { ...tile, currentPosition: targetPos }
          }
          if (tile.id === targetTile.id) {
            return { ...tile, currentPosition: draggedOriginalPos }
          }
          return tile
        })

        const newMergedGroups = checkForMerges(updatedTiles)
        const isComplete = checkWinCondition(updatedTiles)

        return {
          ...prev,
          tiles: updatedTiles,
          mergedGroups: newMergedGroups,
          isComplete,
        }
      })
    } else if (targetGroup) {
      // Un-merge target group and swap with one tile from it
      setGameState(prev => {
        const groupTiles = prev.tiles.filter(t => targetGroup.tileIds.includes(t.id))

        // Find which specific tile in the group we're dropping on (closest one)
        let closestTile = groupTiles[0]
        let minDist = Infinity
        groupTiles.forEach(tile => {
          const dist = Math.abs(tile.currentPosition.x - x) + Math.abs(tile.currentPosition.y - y)
          if (dist < minDist) {
            minDist = dist
            closestTile = tile
          }
        })

        const targetPos = { ...closestTile.currentPosition }

        const updatedTiles = prev.tiles.map(tile => {
          if (tile.id === tileId) {
            // Move dragged tile to the closest tile's position
            return { ...tile, currentPosition: targetPos }
          }
          if (tile.id === closestTile.id) {
            // Swap the closest tile with dragged tile's original position
            return { ...tile, currentPosition: draggedOriginalPos }
          }
          // All other tiles in the group stay where they are (un-merged)
          return tile
        })

        // Re-check merges (the target group is now broken, might form new merges)
        const newMergedGroups = checkForMerges(updatedTiles)
        const isComplete = checkWinCondition(updatedTiles)

        return {
          ...prev,
          tiles: updatedTiles,
          mergedGroups: newMergedGroups,
          isComplete,
        }
      })
    } else {
      // Snap to grid if not dropped on anything
      const gridX = Math.round(x / tileDimensions.width) * tileDimensions.width
      const gridY = Math.round(y / tileDimensions.height) * tileDimensions.height

      setGameState(prev => ({
        ...prev,
        tiles: prev.tiles.map(tile =>
          tile.id === tileId
            ? { ...tile, currentPosition: { x: gridX, y: gridY } }
            : tile
        ),
      }))
    }

    dragStartPos.current = null
  }

  // Check for tiles that should be merged
  const checkForMerges = (tiles: TileData[]): MergedTileGroup[] => {
    const mergedGroups: MergedTileGroup[] = []
    const processedTiles = new Set<string>()

    tiles.forEach(tile => {
      if (processedTiles.has(tile.id)) return

      // Find adjacent tiles in correct relative position
      const group: string[] = [tile.id]
      processedTiles.add(tile.id)

      const findConnected = (currentTile: TileData) => {
        tiles.forEach(otherTile => {
          if (processedTiles.has(otherTile.id)) return

          if (
            areAdjacent(currentTile, otherTile, tileDimensions.width, tileDimensions.height) &&
            areInCorrectRelativePosition(currentTile, otherTile)
          ) {
            group.push(otherTile.id)
            processedTiles.add(otherTile.id)
            findConnected(otherTile) // Recursively find more connected tiles
          }
        })
      }

      findConnected(tile)

      // Only create a group if there are 2 or more tiles
      if (group.length > 1) {
        mergedGroups.push({
          id: `group-${group.join('-')}`,
          tileIds: group,
          position: tile.currentPosition,
        })
      }
    })

    return mergedGroups
  }

  const handleMergedGroupDragStart = (groupId: string) => {
    // Store the original position before drag
    const group = gameState.mergedGroups.find(g => g.id === groupId)
    if (group) {
      // Find the minimum x and y from tiles in the group
      const groupTiles = gameState.tiles.filter(t => group.tileIds.includes(t.id))
      const minX = Math.min(...groupTiles.map(t => t.currentPosition.x))
      const minY = Math.min(...groupTiles.map(t => t.currentPosition.y))
      groupDragStartPos.current = { x: minX, y: minY }
    }
  }

  const handleMergedGroupDragEnd = (groupId: string, x: number, y: number) => {
    const draggedGroup = gameState.mergedGroups.find(g => g.id === groupId)
    if (!draggedGroup || !groupDragStartPos.current) return

    const groupOriginalPos = { ...groupDragStartPos.current }
    const draggedGroupTileIds = draggedGroup.tileIds

    setGameState(prev => {
      // Get current positions of tiles in the dragged group
      const currentGroupTiles = prev.tiles.filter(t => draggedGroupTileIds.includes(t.id))

      // Calculate how far the group has actually moved
      const currentMinX = Math.min(...currentGroupTiles.map(t => t.currentPosition.x))
      const currentMinY = Math.min(...currentGroupTiles.map(t => t.currentPosition.y))

      // Calculate where each tile in the group will land
      const deltaX = x - groupOriginalPos.x
      const deltaY = y - groupOriginalPos.y

      const groupTileNewPositions = new Map<string, Position>()
      const groupTileOldPositions = new Map<string, Position>()

      currentGroupTiles.forEach(tile => {
        // Calculate the tile's position BEFORE the drag
        const offsetFromGroupTopLeft = {
          x: tile.currentPosition.x - currentMinX,
          y: tile.currentPosition.y - currentMinY
        }
        const oldPos = {
          x: groupOriginalPos.x + offsetFromGroupTopLeft.x,
          y: groupOriginalPos.y + offsetFromGroupTopLeft.y
        }
        groupTileOldPositions.set(tile.id, oldPos)

        // Calculate where it will land
        const newPos = {
          x: oldPos.x + deltaX,
          y: oldPos.y + deltaY
        }
        groupTileNewPositions.set(tile.id, newPos)
      })

      // Find ALL tiles that will be overlapped by the dropped group
      const overlappingTiles: TileData[] = []
      prev.tiles.forEach(tile => {
        if (draggedGroupTileIds.includes(tile.id)) return // Skip tiles in the dragged group

        // Check if this tile overlaps with any landing position
        for (const newPos of groupTileNewPositions.values()) {
          const dx = Math.abs(tile.currentPosition.x - newPos.x)
          const dy = Math.abs(tile.currentPosition.y - newPos.y)
          if (dx < tileDimensions.width / 3 && dy < tileDimensions.height / 3) {
            overlappingTiles.push(tile)
            break
          }
        }
      })

      if (overlappingTiles.length > 0) {
        // SWAP: match each overlapping tile with a group tile
        const swapPairs = new Map<string, string>() // targetTileId -> groupTileId
        const usedGroupTiles = new Set<string>()

        overlappingTiles.forEach(targetTile => {
          // Find which group tile is landing on this target
          for (const [groupTileId, newPos] of groupTileNewPositions) {
            if (usedGroupTiles.has(groupTileId)) continue // Already paired

            const dx = Math.abs(targetTile.currentPosition.x - newPos.x)
            const dy = Math.abs(targetTile.currentPosition.y - newPos.y)
            if (dx < tileDimensions.width / 3 && dy < tileDimensions.height / 3) {
              swapPairs.set(targetTile.id, groupTileId)
              usedGroupTiles.add(groupTileId)
              break
            }
          }
        })

        // Apply swaps
        const updatedTiles = prev.tiles.map(tile => {
          // Is this target tile being swapped with a group tile?
          const groupTileSwappingWithMe = swapPairs.get(tile.id)
          if (groupTileSwappingWithMe) {
            const oldPos = groupTileOldPositions.get(groupTileSwappingWithMe)!
            return { ...tile, currentPosition: { ...oldPos } }
          }

          // Is this a group tile?
          if (draggedGroupTileIds.includes(tile.id)) {
            const newPos = groupTileNewPositions.get(tile.id)!
            return { ...tile, currentPosition: { ...newPos } }
          }

          return tile
        })

        const newMergedGroups = checkForMerges(updatedTiles)
        const isComplete = checkWinCondition(updatedTiles)

        return {
          ...prev,
          tiles: updatedTiles,
          mergedGroups: newMergedGroups,
          isComplete,
        }
      } else {
        // Snap to grid if not dropped on anything
        const gridX = Math.round(x / tileDimensions.width) * tileDimensions.width
        const gridY = Math.round(y / tileDimensions.height) * tileDimensions.height
        const snapDeltaX = gridX - groupOriginalPos.x
        const snapDeltaY = gridY - groupOriginalPos.y

        const updatedTiles = prev.tiles.map(tile => {
          if (draggedGroupTileIds.includes(tile.id)) {
            const oldPos = groupTileOldPositions.get(tile.id)!
            return {
              ...tile,
              currentPosition: {
                x: oldPos.x + snapDeltaX,
                y: oldPos.y + snapDeltaY,
              },
            }
          }
          return tile
        })

        const newMergedGroups = checkForMerges(updatedTiles)
        const isComplete = checkWinCondition(updatedTiles)

        return {
          ...prev,
          tiles: updatedTiles,
          mergedGroups: newMergedGroups,
          isComplete,
        }
      }
    })

    groupDragStartPos.current = null
  }

  // Get tiles that are not part of any merged group
  const getIndividualTiles = () => {
    const mergedTileIds = new Set(
      gameState.mergedGroups.flatMap(group => group.tileIds)
    )
    return gameState.tiles.filter(tile => !mergedTileIds.has(tile.id))
  }

  const stageWidth = gameState.gridSize * tileDimensions.width
  const stageHeight = gameState.gridSize * tileDimensions.height

  if (gameState.tiles.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading game...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Stage width={stageWidth} height={stageHeight} ref={stageRef}>
        <Layer>
          {/* Background grid */}
          <Rect
            x={0}
            y={0}
            width={stageWidth}
            height={stageHeight}
            fill="#f0f0f0"
            stroke="#ccc"
            strokeWidth={1}
          />

          {gameState.isComplete ? (
            /* Render complete image as single block when puzzle is solved */
            uploadedImage && (
              <Image
                x={0}
                y={0}
                width={stageWidth}
                height={stageHeight}
                image={uploadedImage}
              />
            )
          ) : (
            <>
              {/* Render merged tile groups first (so they appear below individual tiles) */}
              {gameState.mergedGroups.map(group => (
                <MergedTile
                  key={group.id}
                  group={group}
                  tiles={gameState.tiles.filter(t => group.tileIds.includes(t.id))}
                  tileWidth={tileDimensions.width}
                  tileHeight={tileDimensions.height}
                  onDragStart={handleMergedGroupDragStart}
                  onDragEnd={handleMergedGroupDragEnd}
                />
              ))}

              {/* Render individual tiles last (so they appear on top) */}
              {getIndividualTiles().map(tile => (
                <Tile
                  key={tile.id}
                  tile={tile}
                  tileWidth={tileDimensions.width}
                  tileHeight={tileDimensions.height}
                  onDragStart={handleDragStart}
                  onDragMove={handleDragMove}
                  onDragEnd={handleDragEnd}
                />
              ))}
            </>
          )}
        </Layer>
      </Stage>
    </Box>
  )
}

export default GameBoard
